import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { dailyTranslations } from "./daily/translations";

// The language to start in, known before anything is drawn: the browser's,
// unless the address asks for another with ?lang=de (for checking a
// translation).
function startingLanguage(): string {
  if (typeof window === "undefined") return "en";
  return new URLSearchParams(window.location.search).get("lang") || navigator.language;
}

i18n.use(initReactI18next).init({
  debug: true,
  lng: startingLanguage(),
  fallbackLng: "en",
  resources: {
    en: {
      translation: {
        home: {
          exploreTheSkies: "Explore the skies!",
          instructions: {
            get5RandomFlights:
              "Get 5 random flights and guess the origin and destination with the fewest tries.",
            startWith1000Points:
              "Start each round with 1,000 points, and lose points for each wrong guess or each clue revealed.",
          },
          playButton: "Play",
        },
        gameOptions: {
          startGame: "Start Game",
          difficulty: {
            easy: {
              name: "Easy",
              description: "Big airports only, all shown on the map.",
            },
            medium: {
              name: "Medium",
              description: "Medium and big airports, all shown on the map.",
            },
            hard: {
              name: "Hard",
              description: "All airports, none shown on the map.",
            },
          },
          regions: {
            usa: "United States",
            eur: "Europe",
            world: "Worldwide",
          },
        },
        game: {
          controls: {
            guessTheFlight: "Guess the Flight",
            enterCityOrAirport: "Enter the city or airport",
            origin: "Origin",
            destination: "Destination",
            needAHint: "Need a hint?",
            revealKeyInfo: "Reveal key information for 30 points!",
            hints: {
              airline: "Reveal Airline",
              altitude: "Reveal Altitude",
              duration: "Reveal Duration",
              originTimezone: "Reveal Origin Timezone",
              destinationTimezone: "Reveal Destination Timezone",
              mobile: {
                airline: "Airline",
                altitude: "Altitude",
                duration: "Duration",
                originTimezone: "Origin Timezone",
                destinationTimezone: "Dest. Timezone",
              },
            },
            giveUp: {
              initial: "Give Up",
              confirm: "Are you sure?",
            },
          },
          stats: {
            points: "Points",
            round: "Round",
          },
          distance: {
            guessWas: {
              cold: "Your guess was cold 🥶",
              warm: "Your guess was warm 😅",
              hot: "Your guess was hot 🥵",
            },
            distanceAway: "{{distance}} miles away",
          },
          tipOverlay: {
            gotIt: "Got it",
            intialInstructions: {
              title: "Welcome aboard! 🧑‍✈️",
              startGuessing: "Start Guessing!",
              first:
                "You'll see a random flight <emphasize>happening right now</emphasize> and your goal is to guess its origin and destination airports!",
              second:
                "All the possible airports are shown on the map. When you submit a guess, you'll see how far off you were!",
            },
            origin: {
              title: "Need help getting started?",
              instruction:
                "Tap the <highlight>origin</highlight> field, and guess where the flight is coming from!",
              suggestedAirports: "🤫 Psst, it's one of these: ",
            },
            hint: {
              title: "Feeling stuck? Reveal a hint!",
              instruction:
                "Choose a hint to reveal info about the flight, such as the <highlight>airline, altitude, duration, or timezones</highlight>.",
            },
            gameError: {
              title: "Something went wrong",
              instruction:
                "Please try again. If the problem persists, please contact us! (feedback@flightguesser.com)",
              button: "Try again",
            },
          },
          roundSummary: {
            pointsScored: "points scored in this round!",
            flightDetails: "Flight Details",
            airline: "Airline: ",
            distance: "Distance: ",
            duration: "Duration: ",
            plane: "Plane: ",
            nextRound: "Next Round",
            viewGameSummary: "View Game Summary",
            distanceUnit: "miles",
            durationUnit: "hours",
          },
          gameSummary: {
            totalPoints: "total points scored this game!",
            tripDetails:
              "What a trip! You traveled {{distance}} miles, visited {{countriesString}}, and flew on {{airlinesString}}.",
            countrySingular: "country",
            countryPlural: "countries",
            airlineSingular: "airline",
            airlinePlural: "airlines",
            newGame: "New Game",
            joinFrequentFlyer:
              "Become a Frequent Flyer, get updates, and more!",
            emailPlaceholder: "Email",
            joinButton: "Join",
          },
        },
      },
    },
    es: {
      translation: {
        home: {
          exploreTheSkies: "¡Explora los cielos!",
          instructions: {
            get5RandomFlights:
              "Adivina el origen y el destino de 5 vuelos aleatorios con el menor número de intentos.",
            startWith1000Points:
              "Comienza cada ronda con 1,000 puntos y pierde puntos por cada respuesta incorrecta o pista revelada.",
          },
          playButton: "Jugar",
        },
        gameOptions: {
          startGame: "Iniciar juego",
          difficulty: {
            easy: {
              name: "Fácil",
              description:
                "Solo aeropuertos grandes, todos visibles en el mapa.",
            },
            medium: {
              name: "Medio",
              description:
                "Aeropuertos medianos y grandes, todos visibles en el mapa.",
            },
            hard: {
              name: "Difícil",
              description: "Todos los aeropuertos, ninguno visible en el mapa.",
            },
          },
          regions: {
            usa: "Estados Unidos",
            eur: "Europa",
            world: "Mundial",
          },
        },
        game: {
          controls: {
            guessTheFlight: "Adivina el vuelo",
            enterCityOrAirport: "Ingresa la ciudad o el aeropuerto",
            origin: "Origen",
            destination: "Destino",
            needAHint: "¿Necesitas una pista?",
            revealKeyInfo: "¡Revela información clave por 30 puntos!",
            hints: {
              airline: "Revelar aerolínea",
              altitude: "Revelar altitud",
              duration: "Revelar duración",
              originTimezone: "Revelar zona horaria de origen",
              destinationTimezone: "Revelar zona horaria de destino",
              mobile: {
                airline: "Aerolínea",
                altitude: "Altitud",
                duration: "Duración",
                originTimezone: "Zona horaria de origen",
                destinationTimezone: "Zona horaria de destino",
              },
            },
            giveUp: {
              initial: "Rendirse",
              confirm: "¿Estás seguro?",
            },
          },
          stats: {
            points: "Puntos",
            round: "Ronda",
          },
          distance: {
            guessWas: {
              cold: "Tu suposición fue fría 🥶",
              warm: "Tu suposición fue cálida 😅",
              hot: "Tu suposición fue caliente 🥵",
            },
            distanceAway: "A {{distance}} millas de distancia",
          },
          tipOverlay: {
            gotIt: "¡Entendido!",
            intialInstructions: {
              title: "¡Bienvenido a bordo! 🧑‍✈️",
              startGuessing: "¡Empieza a adivinar!",
              first:
                "¡Verás un vuelo aleatorio <emphasize>que está sucediendo ahora mismo</emphasize> y tu objetivo es adivinar sus aeropuertos de origen y destino!",
              second:
                "Todos los aeropuertos posibles se muestran en el mapa. ¡Cuando envíes una suposición, verás qué tan lejos estabas!",
            },
            origin: {
              title: "¿Necesitas ayuda para comenzar?",
              instruction:
                "Toca el campo de <highlight>origen</highlight> y adivina de dónde viene el vuelo.",
              suggestedAirports: "🤫 Oye, es uno de estos: ",
            },
            hint: {
              title: "¿Atascado? ¡Revela una pista!",
              instruction:
                "Elige una pista para descubrir información sobre el vuelo, como la <highlight>aerolínea, altitud, duración o zonas horarias</highlight>.",
            },
            gameError: {
              title: "Algo salió mal",
              instruction:
                "Por favor, inténtalo de nuevo. Si el problema persiste, contáctanos! (feedback@flightguesser.com)",
              button: "Intentar de nuevo",
            },
          },
          roundSummary: {
            pointsScored: "puntos obtenidos en esta ronda!",
            flightDetails: "Detalles del vuelo",
            airline: "Aerolínea: ",
            distance: "Distancia: ",
            duration: "Duración: ",
            plane: "Avión: ",
            nextRound: "Siguiente ronda",
            viewGameSummary: "Ver resumen del juego",
            distanceUnit: "millas",
            durationUnit: "horas",
          },
          gameSummary: {
            totalPoints: "puntos totales obtenidos en este juego!",
            tripDetails:
              "¡Qué viaje! Viajaste {{distance}} millas, visitaste {{countriesString}}, y volaste con {{airlinesString}}.",
            countrySingular: "país",
            countryPlural: "países",
            airlineSingular: "aerolínea",
            airlinePlural: "aerolíneas",
            newGame: "Nuevo Juego",
            joinFrequentFlyer: "Suscríbete para recibir novedades.",
            emailPlaceholder: "Correo electrónico",
            joinButton: "Unirse",
            thanksForSubscribing: "¡Gracias por suscribirte!",
            somethingWentWrong:
              "Algo salió mal. Por favor, inténtalo de nuevo.",
          },
        },
      },
    },

    de: {
      translation: {
        home: {
          exploreTheSkies: "Erkunde den Himmel!",
          instructions: {
            get5RandomFlights:
              "Errate den Ursprung und das Ziel von 5 zufälligen Flügen mit den wenigsten Versuchen.",
            startWith1000Points:
              "Beginne jede Runde mit 1.000 Punkten und verliere Punkte für jede falsche Antwort oder enthüllten Hinweis.",
          },
          playButton: "Spielen",
        },
        gameOptions: {
          startGame: "Spiel starten",
          difficulty: {
            easy: {
              name: "Leicht",
              description: "Nur große Flughäfen, alle auf der Karte sichtbar.",
            },
            medium: {
              name: "Mittel",
              description:
                "Mittlere und große Flughäfen, alle auf der Karte sichtbar.",
            },
            hard: {
              name: "Schwer",
              description: "Alle Flughäfen, keine auf der Karte sichtbar.",
            },
          },
          regions: {
            usa: "USA",
            eur: "Europa",
            world: "Weltweit",
          },
        },
        game: {
          controls: {
            guessTheFlight: "Errate den Flug",
            enterCityOrAirport: "Gib die Stadt oder den Flughafen ein",
            origin: "Herkunft",
            destination: "Ziel",
            needAHint: "Brauchst du einen Hinweis?",
            revealKeyInfo: "Enthülle wichtige Informationen für 30 Punkte!",
            hints: {
              airline: "Fluggesellschaft anzeigen",
              altitude: "Höhe anzeigen",
              duration: "Dauer anzeigen",
              originTimezone: "Zeitzone des Ursprungs anzeigen",
              destinationTimezone: "Zeitzone des Ziels anzeigen",
              mobile: {
                airline: "Fluggesellschaft",
                altitude: "Höhe",
                duration: "Dauer",
                originTimezone: "Ursprungszeitzone",
                destinationTimezone: "Zielzeitzone",
              },
            },
            giveUp: {
              initial: "Aufgeben",
              confirm: "Bist du sicher?",
            },
          },
          stats: {
            points: "Punkte",
            round: "Runde",
          },
          distance: {
            guessWas: {
              cold: "Dein Tipp war kalt 🥶",
              warm: "Dein Tipp war warm 😅",
              hot: "Dein Tipp war heiß 🥵",
            },
            distanceAway: "{{distance}} Meilen entfernt",
          },
          tipOverlay: {
            gotIt: "Verstanden!",
            intialInstructions: {
              title: "Willkommen an Bord! 🧑‍✈️",
              startGuessing: "Fang an zu raten!",
              first:
                "Du siehst einen zufälligen Flug, der <emphasize>gerade jetzt stattfindet</emphasize>, und dein Ziel ist es, seine Start- und Zielflughäfen zu erraten!",
              second:
                "Alle möglichen Flughäfen sind auf der Karte zu sehen. Wenn du eine Vermutung abgibst, siehst du, wie weit du daneben lagst!",
            },
            origin: {
              title: "Brauchst du Hilfe beim Einstieg?",
              instruction:
                "Tippe auf das Feld <highlight>Herkunft</highlight> und rate, woher der Flug kommt!",
              suggestedAirports: "🤫 Hey, es ist einer von diesen: ",
            },
            hint: {
              title: "Festgefahren? Enthülle einen Hinweis!",
              instruction:
                "Wähle einen Hinweis, um Informationen über den Flug zu erhalten, wie die <highlight>Fluggesellschaft, Höhe, Dauer oder Zeitzonen</highlight>.",
            },
            gameError: {
              title: "Etwas ist schiefgelaufen",
              instruction:
                "Bitte versuche es erneut. Wenn das Problem weiterhin besteht, kontaktiere uns bitte! (feedback@flightguesser.com)",
              button: "Erneut versuchen",
            },
          },
          roundSummary: {
            pointsScored: "Punkte in dieser Runde erzielt!",
            flightDetails: "Flugdetails",
            airline: "Fluggesellschaft: ",
            distance: "Entfernung: ",
            duration: "Dauer: ",
            plane: "Flugzeug: ",
            nextRound: "Nächste Runde",
            viewGameSummary: "Spielzusammenfassung ansehen",
            distanceUnit: "Meilen",
            durationUnit: "Stunden",
          },
          gameSummary: {
            totalPoints: "gesamtpunkte in diesem Spiel erzielt!",
            tripDetails:
              "Was für eine Reise! Du bist {{distance}} Meilen gereist, hast {{countriesString}} besucht und bist mit {{airlinesString}} geflogen.",
            countrySingular: "Land",
            countryPlural: "Länder",
            airlineSingular: "Fluggesellschaft",
            airlinePlural: "Fluggesellschaften",
            newGame: "Neues Spiel",
            joinFrequentFlyer: "Abonniere für Neuigkeiten.",
            emailPlaceholder: "E-Mail",
            joinButton: "Beitreten",
            thanksForSubscribing: "Vielen Dank für dein Abonnement!",
            somethingWentWrong:
              "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
          },
        },
      },
    },

    it: {
      translation: {
        home: {
          exploreTheSkies: "Esplora i cieli!",
          instructions: {
            get5RandomFlights:
              "Indovina l'origine e la destinazione di 5 voli casuali con il minor numero di tentativi.",
            startWith1000Points:
              "Inizia ogni round con 1.000 punti e perdi punti per ogni risposta sbagliata o indizio rivelato.",
          },
          playButton: "Gioca",
        },
        gameOptions: {
          startGame: "Inizia gioco",
          difficulty: {
            easy: {
              name: "Facile",
              description: "Solo grandi aeroporti, tutti visibili sulla mappa.",
            },
            medium: {
              name: "Medio",
              description:
                "Aeroporti medi e grandi, tutti visibili sulla mappa.",
            },
            hard: {
              name: "Difficile",
              description: "Tutti gli aeroporti, nessuno visibile sulla mappa.",
            },
          },
          regions: {
            usa: "USA",
            eur: "Europa",
            world: "Mondiale",
          },
        },
        game: {
          controls: {
            guessTheFlight: "Indovina il volo",
            enterCityOrAirport: "Inserisci la città o l'aeroporto",
            origin: "Origine",
            destination: "Destinazione",
            needAHint: "Hai bisogno di un aiuto?",
            revealKeyInfo: "Scopri informazioni chiave per 30 punti!",
            hints: {
              airline: "Rivela compagnia aerea",
              altitude: "Rivela altitudine",
              duration: "Rivela durata",
              originTimezone: "Fuso orario di origine",
              destinationTimezone: "Fuso orario di destinazione",
              mobile: {
                airline: "Compagnia",
                altitude: "Altitudine",
                duration: "Durata",
                originTimezone: "Fuso origine",
                destinationTimezone: "Fuso dest.",
              },
            },
            giveUp: {
              initial: "Arrenditi",
              confirm: "Sei sicuro?",
            },
          },
          stats: {
            points: "Punti",
            round: "Turno",
          },
          distance: {
            guessWas: {
              cold: "La tua risposta era fredda 🥶",
              warm: "La tua risposta era tiepida 😅",
              hot: "La tua risposta era calda 🥵",
            },
            distanceAway: "A {{distance}} miglia di distanza",
          },
          tipOverlay: {
            gotIt: "Ho capito!",
            intialInstructions: {
              title: "Benvenuto a bordo! 🧑‍✈️",
              startGuessing: "Inizia a indovinare!",
              first:
                "Vedrai un volo casuale <emphasize>in corso ora</emphasize> e il tuo obiettivo è indovinare i suoi aeroporti di origine e destinazione!",
              second:
                "Tutti gli aeroporti possibili sono mostrati sulla mappa. Quando invii una risposta, vedrai quanto ti sei avvicinato!",
            },
            origin: {
              title: "Hai bisogno di aiuto per iniziare?",
              instruction:
                "Tocca il campo <highlight>origine</highlight> e indovina da dove parte il volo!",
              suggestedAirports: "🤫 Ehi, è uno di questi: ",
            },
            hint: {
              title: "Bloccato? Rivela un indizio!",
              instruction:
                "Scegli un indizio per scoprire informazioni sul volo, come la <highlight>compagnia aerea, altitudine, durata o fusi orari</highlight>.",
            },
            gameError: {
              title: "Qualcosa è andato storto",
              instruction:
                "Per favore, riprova. Se il problema persiste, contattaci! (feedback@flightguesser.com)",
              button: "Riprova",
            },
          },
          roundSummary: {
            pointsScored: "punti segnati in questo turno!",
            flightDetails: "Dettagli del volo",
            airline: "Compagnia aerea: ",
            distance: "Distanza: ",
            duration: "Durata: ",
            plane: "Aereo: ",
            nextRound: "Prossimo turno",
            viewGameSummary: "Visualizza riepilogo partita",
            distanceUnit: "miglia",
            durationUnit: "ore",
          },
          gameSummary: {
            totalPoints: "punti totali segnati in questo gioco!",
            tripDetails:
              "Che viaggio! Hai percorso {{distance}} miglia, visitato {{countriesString}} e volato con {{airlinesString}}.",
            countrySingular: "paese",
            countryPlural: "paesi",
            airlineSingular: "compagnia aerea",
            airlinePlural: "compagnie aeree",
            newGame: "Nuovo Gioco",
            joinFrequentFlyer: "Iscriviti per ricevere aggiornamenti.",
            emailPlaceholder: "Email",
            joinButton: "Iscriviti",
            thanksForSubscribing: "Grazie per esserti iscritto!",
            somethingWentWrong: "Qualcosa è andato storto. Per favore riprova.",
          },
        },
      },
    },
    fr: {
      translation: {
        home: {
          exploreTheSkies: "Explorez les cieux !",
          instructions: {
            get5RandomFlights:
              "Devinez l'origine et la destination de 5 vols aléatoires avec le moins d'essais possible.",
            startWith1000Points:
              "Commencez chaque manche avec 1 000 points et perdez des points à chaque mauvaise réponse ou indice révélé.",
          },
          playButton: "Jouer",
        },
        gameOptions: {
          startGame: "Commencer le jeu",
          difficulty: {
            easy: {
              name: "Facile",
              description:
                "Seulement les grands aéroports, tous visibles sur la carte.",
            },
            medium: {
              name: "Moyen",
              description:
                "Aéroports moyens et grands, tous visibles sur la carte.",
            },
            hard: {
              name: "Difficile",
              description: "Tous les aéroports, aucun visible sur la carte.",
            },
          },
          regions: {
            usa: "USA",
            eur: "Europe",
            world: "Mondiale",
          },
        },
        game: {
          controls: {
            guessTheFlight: "Devinez le vol",
            enterCityOrAirport: "Entrez la ville ou l'aéroport",
            origin: "Origine",
            destination: "Destination",
            needAHint: "Besoin d'un indice ?",
            revealKeyInfo: "Révélez des informations clés pour 30 points !",
            hints: {
              airline: "Révéler la compagnie aérienne",
              altitude: "Révéler l'altitude",
              duration: "Révéler la durée",
              originTimezone: "Fuseau horaire origine",
              destinationTimezone: "Fuseau horaire destination",
              mobile: {
                airline: "Compagnie",
                altitude: "Altitude",
                duration: "Durée",
                originTimezone: "TZ origine",
                destinationTimezone: "TZ dest.",
              },
            },
            giveUp: {
              initial: "Abandonner",
              confirm: "Êtes-vous sûr ?",
            },
          },
          stats: {
            points: "Points ",
            round: "Tour ",
          },
          distance: {
            guessWas: {
              cold: "Votre supposition était froide 🥶",
              warm: "Votre supposition était tiède 😅",
              hot: "Votre supposition était chaude 🥵",
            },
            distanceAway: "À {{distance}} miles",
          },
          tipOverlay: {
            gotIt: "Compris !",
            intialInstructions: {
              title: "Bienvenue à bord ! 🧑‍✈️",
              startGuessing: "Commencez à deviner !",
              first:
                "Vous verrez un vol aléatoire <emphasize>en cours actuellement</emphasize> et votre objectif est de deviner ses aéroports de départ et d'arrivée !",
              second:
                "Tous les aéroports possibles sont affichés sur la carte. Quand vous soumettez une réponse, vous verrez à quel point vous étiez proche !",
            },
            origin: {
              title: "Besoin d'aide pour commencer ?",
              instruction:
                "Appuyez sur le champ <highlight>origine</highlight> et devinez d'où vient le vol !",
              suggestedAirports: "🤫 Hé, c'est l'un de ceux-ci : ",
            },
            hint: {
              title: "Bloqué ? Révélez un indice !",
              instruction:
                "Choisissez un indice pour révéler des informations sur le vol, comme la <highlight>compagnie aérienne, l'altitude, la durée ou les fuseaux horaires</highlight>.",
            },
            gameError: {
              title: "Quelque chose a mal tourné",
              instruction:
                "Veuillez réessayer. Si le problème persiste, veuillez nous contacter ! (feedback@flightguesser.com)",
              button: "Réessayer",
            },
          },
          roundSummary: {
            pointsScored: "points marqués dans ce tour !",
            flightDetails: "Détails du vol",
            airline: "Compagnie aérienne : ",
            distance: "Distance : ",
            duration: "Durée : ",
            plane: "Avion : ",
            nextRound: "Tour suivant",
            viewGameSummary: "Voir le résumé du jeu",
            distanceUnit: "miles",
            durationUnit: "heures",
          },
          gameSummary: {
            totalPoints: "points totaux marqués dans ce jeu !",
            tripDetails:
              "Quel voyage ! Vous avez parcouru {{distance}} miles, visité {{countriesString}} et voyagé avec {{airlinesString}}.",
            countrySingular: "pays",
            countryPlural: "pays",
            airlineSingular: "compagnie aérienne",
            airlinePlural: "compagnies aériennes",
            newGame: "Nouvelle Partie",
            joinFrequentFlyer: "Inscrivez-vous pour recevoir des mises à jour.",
            emailPlaceholder: "Email",
            joinButton: "Rejoindre",
            thanksForSubscribing: "Merci pour votre inscription !",
            somethingWentWrong: "Un problème est survenu. Veuillez réessayer.",
          },
        },
      },
    },
    fi: {
      translation: {
        home: {
          exploreTheSkies: "Tutki taivaita!",
          instructions: {
            get5RandomFlights:
              "Arvaa 5 satunnaisen lennon lähtöpaikka ja kohde mahdollisimman vähillä yrityksillä.",
            startWith1000Points:
              "Aloita jokainen kierros 1 000 pisteellä ja menetä pisteitä jokaisesta väärästä arvauksesta tai vihjeestä, jonka paljastat.",
          },
          playButton: "Pelaa",
        },
        gameOptions: {
          startGame: "Aloita peli",
          difficulty: {
            easy: {
              name: "Helppo",
              description:
                "Vain suuret lentokentät, kaikki näkyvissä kartalla.",
            },
            medium: {
              name: "Keskitaso",
              description:
                "Keskikokoiset ja suuret lentokentät, kaikki näkyvissä kartalla.",
            },
            hard: {
              name: "Vaikea",
              description: "Kaikki lentokentät, ei yhtään näkyvissä kartalla.",
            },
          },
          regions: {
            usa: "USA",
            eur: "Eurooppa",
            world: "Maaailma",
          },
        },
        game: {
          controls: {
            guessTheFlight: "Arvaa lento",
            enterCityOrAirport: "Syötä kaupunki tai lentokenttä",
            origin: "Lähtö",
            destination: "Määränpää",
            needAHint: "Tarvitsetko vihjeen?",
            revealKeyInfo: "Paljasta avaintiedot 30 pisteellä!",
            hints: {
              airline: "Paljasta lentoyhtiö",
              altitude: "Paljasta korkeus",
              duration: "Paljasta kesto",
              originTimezone: "Aikavyöhyke lähtö",
              destinationTimezone: "Aikavyöhyke määränpää",
              mobile: {
                airline: "Lentoyhtiö",
                altitude: "Korkeus",
                duration: "Kesto",
                originTimezone: "Aikavyöhyke lähtö",
                destinationTimezone: "Aikavyöhyke määränpää",
              },
            },
            giveUp: {
              initial: "Luovuta",
              confirm: "Oletko varma?",
            },
          },
          stats: {
            points: "Pisteet",
            round: "Kierros",
          },
          distance: {
            guessWas: {
              cold: "Arvauksesi oli kylmä 🥶",
              warm: "Arvauksesi oli lämmin 😅",
              hot: "Arvauksesi oli kuuma 🥵",
            },
            distanceAway: "{{distance}} mailia pois",
          },
          tipOverlay: {
            gotIt: "Selvä!",
            intialInstructions: {
              title: "Tervetuloa kyytiin! 🧑‍✈️",
              startGuessing: "Aloita arvaaminen!",
              first:
                "Näet satunnaisen lennon, joka <emphasize>tapahtuu juuri nyt</emphasize>, ja tavoitteesi on arvata sen lähtö- ja määränpääkentät!",
              second:
                "Kaikki mahdolliset lentokentät näkyvät kartalla. Kun lähetät arvauksen, näet kuinka kaukana olit!",
            },
            origin: {
              title: "Tarvitsetko apua aloittamiseen?",
              instruction:
                "Napauta kenttää <highlight>lähtö</highlight> ja arvaa, mistä lento tulee!",
              suggestedAirports: "🤫 Hei, se on yksi näistä: ",
            },
            hint: {
              title: "Jumissa? Paljasta vihje!",
              instruction:
                "Valitse vihje saadaksesi tietoa lennosta, kuten <highlight>lentoyhtiö, korkeus, kesto tai aikavyöhykkeet</highlight>.",
            },
            gameError: {
              title: "Jotain meni pieleen",
              instruction:
                "Yritä uudelleen. Jos ongelma jatkuu, ota meihin yhteyttä! (feedback@flightguesser.com)",
              button: "Yritä uudelleen",
            },
          },
          roundSummary: {
            pointsScored: "pistettä tällä kierroksella!",
            flightDetails: "Lennon tiedot",
            airline: "Lentoyhtiö: ",
            distance: "Etäisyys: ",
            duration: "Kesto: ",
            plane: "Lentokone: ",
            nextRound: "Seuraava kierros",
            viewGameSummary: "Näytä pelin yhteenveto",
            distanceUnit: "mailia",
            durationUnit: "tuntia",
          },
          gameSummary: {
            totalPoints: "kokonaispisteet tässä pelissä!",
            tripDetails:
              "Mikä matka! Matkustit {{distance}} mailia, vierailit {{countriesString}} ja lensit {{airlinesString}} kanssa.",
            countrySingular: "maa",
            countryPlural: "maat",
            airlineSingular: "lentoyhtiö",
            airlinePlural: "lentoyhtiöt",
            newGame: "Uusi Peli",
            joinFrequentFlyer: "Tilaa saadaksesi päivityksiä.",
            emailPlaceholder: "Sähköposti",
            joinButton: "Liity",
            thanksForSubscribing: "Kiitos tilaamisesta!",
            somethingWentWrong: "Jotain meni pieleen. Yritä uudelleen.",
          },
        },
      },
    },
    sv: {
      translation: {
        home: {
          exploreTheSkies: "Utforska himlen!",
          instructions: {
            get5RandomFlights:
              "Gissa ursprunget och destinationen för 5 slumpmässiga flygningar med så få försök som möjligt.",
            startWith1000Points:
              "Börja varje runda med 1 000 poäng och förlora poäng för varje fel svar eller avslöjad ledtråd.",
          },
          playButton: "Spela",
        },
        gameOptions: {
          startGame: "Starta spelet",
          difficulty: {
            easy: {
              name: "Lätt",
              description: "Bara stora flygplatser, alla syns på kartan.",
            },
            medium: {
              name: "Medel",
              description:
                "Medelstora och stora flygplatser, alla syns på kartan.",
            },
            hard: {
              name: "Svår",
              description: "Alla flygplatser, inga syns på kartan.",
            },
          },
          regions: {
            usa: "USA",
            eur: "Europa",
            world: "Maaailma",
          },
        },
        game: {
          controls: {
            guessTheFlight: "Gissa flyget",
            enterCityOrAirport: "Ange stad eller flygplats",
            origin: "Ursprung",
            destination: "Destination",
            needAHint: "Behöver du en ledtråd?",
            revealKeyInfo: "Visa viktig information för 30 poäng!",
            hints: {
              airline: "Visa flygbolag",
              altitude: "Visa höjd",
              duration: "Visa varaktighet",
              originTimezone: "Visa tidszon ursprung",
              destinationTimezone: "Visa tidszon destination",
              mobile: {
                airline: "Flygbolag",
                altitude: "Höjd",
                duration: "Varaktighet",
                originTimezone: "Tidszon ursprung",
                destinationTimezone: "Tidszon destination",
              },
            },
            giveUp: {
              initial: "Ge upp",
              confirm: "Är du säker?",
            },
          },
          stats: {
            points: "Poäng",
            round: "Omgång",
          },
          distance: {
            guessWas: {
              cold: "Din gissning var kall 🥶",
              warm: "Din gissning var varm 😅",
              hot: "Din gissning var het 🥵",
            },
            distanceAway: "{{distance}} miles bort",
          },
          tipOverlay: {
            gotIt: "Uppfattat!",
            intialInstructions: {
              title: "Välkommen ombord! 🧑‍✈️",
              startGuessing: "Börja gissa!",
              first:
                "Du kommer att se ett slumpmässigt flyg som <emphasize>pågår just nu</emphasize> och ditt mål är att gissa dess avgångs- och destinationsflygplatser!",
              second:
                "Alla möjliga flygplatser visas på kartan. När du skickar in en gissning får du se hur långt ifrån du var!",
            },
            origin: {
              title: "Behöver du hjälp att komma igång?",
              instruction:
                "Tryck på fältet <highlight>ursprung</highlight> och gissa varifrån flyget kommer!",
              suggestedAirports: "🤫 Hej, det är en av dessa: ",
            },
            hint: {
              title: "Fastnat? Avslöja en ledtråd!",
              instruction:
                "Välj en ledtråd för att få information om flyget, såsom <highlight>flygbolag, höjd, varaktighet eller tidszoner</highlight>.",
            },
            gameError: {
              title: "Något gick fel",
              instruction:
                "Försök igen. Om problemet kvarstår, kontakta oss! (feedback@flightguesser.com)",
              button: "Försök igen",
            },
          },
          roundSummary: {
            pointsScored: "poäng i denna omgång!",
            flightDetails: "Flyginformation",
            airline: "Flygbolag: ",
            distance: "Avstånd: ",
            duration: "Varaktighet: ",
            plane: "Flygplan: ",
            nextRound: "Nästa omgång",
            viewGameSummary: "Visa spelsammanfattning",
            distanceUnit: "miles",
            durationUnit: "timmar",
          },
          gameSummary: {
            totalPoints: "totala poäng i detta spel!",
            tripDetails:
              "Vilken resa! Du reste {{distance}} miles, besökte {{countriesString}} och flög med {{airlinesString}}.",
            countrySingular: "land",
            countryPlural: "länder",
            airlineSingular: "flygbolag",
            airlinePlural: "flygbolag",
            newGame: "Nytt Spel",
            joinFrequentFlyer: "Prenumerera för att få uppdateringar.",
            emailPlaceholder: "E-post",
            joinButton: "Gå med",
            thanksForSubscribing: "Tack för din prenumeration!",
            somethingWentWrong: "Något gick fel. Försök igen.",
          },
        },
      },
    },
    nl: {
      translation: {
        home: {
          exploreTheSkies: "Verken de lucht!",
          instructions: {
            get5RandomFlights:
              "Raad de herkomst en bestemming van 5 willekeurige vluchten met zo min mogelijk pogingen.",
            startWith1000Points:
              "Begin elke ronde met 1.000 punten en verlies punten voor elk fout antwoord of onthulde hint.",
          },
          playButton: "Spelen",
        },
        gameOptions: {
          startGame: "Start spel",
          difficulty: {
            easy: {
              name: "Makkelijk",
              description:
                "Alleen grote luchthavens, allemaal zichtbaar op de kaart.",
            },
            medium: {
              name: "Gemiddeld",
              description:
                "Middelgrote en grote luchthavens, allemaal zichtbaar op de kaart.",
            },
            hard: {
              name: "Moeilijk",
              description: "Alle luchthavens, geen zichtbaar op de kaart.",
            },
          },
          regions: {
            usa: "USA",
            eur: "Europa",
            world: "Wereldwijd",
          },
        },
        game: {
          controls: {
            guessTheFlight: "Raad de vlucht",
            enterCityOrAirport: "Voer de stad of luchthaven in",
            origin: "Vertrek",
            destination: "Bestemming",
            needAHint: "Heb je een hint nodig?",
            revealKeyInfo: "Onthul belangrijke informatie voor 30 punten!",
            hints: {
              airline: "Onthul luchtvaartmaatschappij",
              altitude: "Onthul hoogte",
              duration: "Onthul duur",
              originTimezone: "Tijdzone vertrek",
              destinationTimezone: "Tijdzone bestemming",
              mobile: {
                airline: "Luchtvaart",
                altitude: "Hoogte",
                duration: "Duur",
                originTimezone: "Tijdzone vertrek",
                destinationTimezone: "Tijdzone bestemming",
              },
            },
            giveUp: {
              initial: "Opgeven",
              confirm: "Weet je het zeker?",
            },
          },
          stats: {
            points: "Punten",
            round: "Ronde",
          },
          distance: {
            guessWas: {
              cold: "Je gok was koud 🥶",
              warm: "Je gok was warm 😅",
              hot: "Je gok was heet 🥵",
            },
            distanceAway: "{{distance}} mijl verderop",
          },
          tipOverlay: {
            gotIt: "Begrepen!",
            intialInstructions: {
              title: "Welkom aan boord! 🧑‍✈️",
              startGuessing: "Begin met raden!",
              first:
                "Je ziet een willekeurige vlucht die <emphasize>nu plaatsvindt</emphasize> en je doel is om de vertrek- en bestemmingsluchthavens te raden!",
              second:
                "Alle mogelijke luchthavens worden op de kaart getoond. Wanneer je een gok instuurt, zie je hoe ver je ernaast zat!",
            },
            origin: {
              title: "Heb je hulp nodig om te beginnen?",
              instruction:
                "Tik op het veld <highlight>vertrek</highlight> en raad waar de vlucht vandaan komt!",
              suggestedAirports: "🤫 Hey, het is een van deze: ",
            },
            hint: {
              title: "Blockeert? Ontdek een tip!",
              instruction:
                "Kies een tip om informatie over het vliegtuig te krijgen, zoals de <highlight>luchtvaartmaatschappij, hoogte, duur of tijdzone</highlight>.",
            },
            gameError: {
              title: "Er is iets misgegaan",
              instruction:
                "Probeer het opnieuw. Als het probleem blijft bestaan, neem dan contact met ons op! (feedback@flightguesser.com)",
              button: "Probeer opnieuw",
            },
          },
          roundSummary: {
            pointsScored: "punten gescoord in deze ronde!",
            flightDetails: "Vluchtdetails",
            airline: "Luchtvaartmaatschappij: ",
            distance: "Afstand: ",
            duration: "Duur: ",
            plane: "Vliegtuig: ",
            nextRound: "Volgende ronde",
            viewGameSummary: "Bekijk speloverzicht",
            distanceUnit: "mijl",
            durationUnit: "uur",
          },
          gameSummary: {
            totalPoints: "totale punten gescoord in dit spel!",
            tripDetails:
              "Wat een reis! Je reisde {{distance}} mijl, bezocht {{countriesString}} en vloog met {{airlinesString}}.",
            countrySingular: "land",
            countryPlural: "landen",
            airlineSingular: "luchtvaartmaatschappij",
            airlinePlural: "luchtvaartmaatschappijen",
            newGame: "Nieuw Spel",
            joinFrequentFlyer: "Abonneer je voor updates.",
            emailPlaceholder: "E-mail",
            joinButton: "Meedoen",
            thanksForSubscribing: "Bedankt voor je inschrijving!",
            somethingWentWrong: "Er is iets misgegaan. Probeer het opnieuw.",
          },
        },
      },
    },
  },
});

// Daily Service keeps its text in its own file, under "daily".
Object.entries(dailyTranslations).forEach(([language, strings]) =>
  i18n.addResourceBundle(language, "translation", { daily: strings }, true, true)
);

export default i18n;
